/**
 * Tab „Konten“ (/urlaub, Gate personal_schreiben): Mitarbeiter-Liste mit
 * Urlaubskonto-Karte (alle Saldo-Felder, „Verbleibend“ prominent) und
 * Pflege-Dialog (Anspruch/Zusatztage/Übertrag/Verfall/Anfangssaldo, AP 3.3).
 */

import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pencil } from "lucide-react"
import { toast } from "sonner"
import { LoadingSpinner } from "@/components/shared"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Select } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { api, apiError } from "@/services/api"
import type { LeaveBalance } from "@/types"
import { LeaveBalanceCard } from "./LeaveBalanceCard"

function AccountEditDialog({
  balance,
  employeeName,
  onClose,
}: {
  balance: LeaveBalance
  employeeName: string
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [entitlement, setEntitlement] = useState(fmtDaysInput(balance.entitlementDays))
  const [additional, setAdditional] = useState(fmtDaysInput(balance.additionalDays))
  const [carryover, setCarryover] = useState(fmtDaysInput(balance.carryoverDays))
  const [expiresAt, setExpiresAt] = useState(balance.carryoverExpiresAt ?? "")
  const [initial, setInitial] = useState(fmtDaysInput(balance.initialBalanceDays))

  function fmtDaysInput(value: string | number): string {
    const n = Number(value)
    return Number.isNaN(n) ? "0" : String(n)
  }

  const save = useMutation({
    mutationFn: () =>
      api.leave.account.update(balance.employeeId, balance.year, {
        entitlementDays: Number(entitlement),
        additionalDays: Number(additional),
        carryoverDays: Number(carryover),
        carryoverExpiresAt: expiresAt || null,
        initialBalanceDays: Number(initial),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leave"] })
      toast.success("Urlaubskonto aktualisiert")
      onClose()
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const numbersValid = [entitlement, additional, carryover, initial].every(
    (v) => v !== "" && !Number.isNaN(Number(v))
  )

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Urlaubskonto pflegen — {employeeName} ({balance.year})
          </DialogTitle>
          <DialogDescription>
            Genommen/Beantragt werden aus den Anträgen berechnet und sind hier nicht änderbar.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Anspruch (Tage) *</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                value={entitlement}
                onChange={(e) => setEntitlement(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Zusatztage *</Label>
              <Input
                type="number"
                step="0.5"
                value={additional}
                onChange={(e) => setAdditional(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Übertrag (Tage) *</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                value={carryover}
                onChange={(e) => setCarryover(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Übertrag verfällt am</Label>
              <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Anfangssaldo (Tage) *</Label>
            <Input
              type="number"
              step="0.5"
              value={initial}
              onChange={(e) => setInitial(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button disabled={!numbersValid || save.isPending} onClick={() => save.mutate()}>
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function AccountsTab() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const [selectedId, setSelectedId] = useState("")
  const [editOpen, setEditOpen] = useState(false)

  const employees = useQuery({
    queryKey: ["employees", { size: 100 }],
    queryFn: () => api.employees.list({ size: 100 }),
  })
  const employeeList = employees.data?.data ?? []

  // Ersten Mitarbeiter vorauswaehlen, sobald die Liste da ist
  useEffect(() => {
    const first = employees.data?.data[0]
    if (!selectedId && first) setSelectedId(first.id)
  }, [selectedId, employees.data])

  const balance = useQuery({
    queryKey: ["leave", "account", selectedId, year],
    queryFn: () => api.leave.account.get(selectedId, year),
    enabled: selectedId !== "",
  })

  const selectedName =
    employeeList.find((e) => e.id === selectedId)?.displayName ?? `Mitarbeiter #${selectedId}`

  return (
    <div className="space-y-4">
      <div className="w-32 space-y-1.5">
        <Label>Jahr</Label>
        <Select value={String(year)} onChange={(e) => setYear(Number(e.target.value))}>
          {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
      </div>

      <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
        {/* Mitarbeiter-Liste */}
        <Card>
          <CardContent className="py-2">
            {employees.isLoading ? (
              <LoadingSpinner fullPage />
            ) : (
              <ScrollArea className="max-h-[28rem]">
                <div className="flex flex-col gap-0.5 py-1">
                  {employeeList.map((emp) => (
                    <button
                      key={emp.id}
                      type="button"
                      onClick={() => setSelectedId(emp.id)}
                      className={cn(
                        "rounded-md px-3 py-1.5 text-left text-sm transition-colors",
                        emp.id === selectedId
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-accent"
                      )}
                    >
                      {emp.displayName}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Balance-Karte + Pflege */}
        <div>
          {balance.isLoading ? (
            <LoadingSpinner fullPage />
          ) : balance.data ? (
            <LeaveBalanceCard
              balance={balance.data}
              title={`${selectedName} — Urlaubskonto ${year}`}
              actions={
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil /> Konto pflegen
                </Button>
              }
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              {selectedId
                ? balance.error
                  ? apiError(balance.error)
                  : "Kein Konto geladen."
                : "Mitarbeiter wählen."}
            </p>
          )}
        </div>
      </div>

      {editOpen && balance.data && (
        <AccountEditDialog
          balance={balance.data}
          employeeName={selectedName}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  )
}
